import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { DiagnosticReport } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface UploadResultsReportParams {
  file: File;
  reportReceivedDate: string;
  status: string;
}

interface UploadResultsReportResponse {
  diagnosticReport: DiagnosticReport;
}

export const useUploadResultsReport = ({
  patientId,
  serviceRequestId,
  onSuccess,
}: {
  patientId: string;
  serviceRequestId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<UploadResultsReportResponse, UploadResultsReportParams>(
    {
      mutationOptions: {
        mutationKey:
          QueryKeys.Patient.MedicalRecords.Labs.UPLOAD_RESULTS_REPORT(
            patientId,
            serviceRequestId,
          ),
        mutationFn: ({
          file,
          reportReceivedDate,
          status,
        }: UploadResultsReportParams) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("serviceRequestId", serviceRequestId);
          formData.append("reportReceivedDate", reportReceivedDate);
          formData.append("status", status);
          return axiosInstance.post(
            "/labs/results/upload-results-pdf",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            },
          );
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Labs.DEFAULT(patientId),
          });
          onSuccess?.();
        },
      },
    },
  );
};
